import { useState } from 'react';
import { useSelector } from 'react-redux';
import { createTheme } from '@mui/material/styles';

import chakratheme from '@/chakratheme';

// MUI's own untouched defaults, computed once. Some pages still render MUI v5
// components (Dialog, Menu, Popper, ...) under the app's single top-level
// ChakraProvider; their Fade/Grow/Modal internals call MUI's useTheme(),
// which — since MUI and Chakra share the same underlying Emotion theme
// context — resolves to whatever theme that ChakraProvider was given. Those
// internals read theme.transitions/theme.zIndex, which Chakra's own theme
// never defines, so we borrow just those two namespaces from MUI rather than
// round-tripping the whole theme through MUI again (that previously
// clobbered theme.shadows — see git history on this file).
const muiDefaults = createTheme();

/**
 * @description Returns the Chakra theme directly, with initialColorMode
 * overridden to match the darkMode setting present at mount. Captured once
 * (not recomputed per render) because Chakra's ColorModeProvider re-runs its
 * own mount-sync effect whenever initialColorMode changes, fighting any
 * later runtime color-mode switch (e.g. via useColorMode()/setColorMode()).
 *
 * Only the single top-level ChakraProvider (src/lib/data-router/index.jsx)
 * should consume this. A page-level ChakraProvider fed by this hook spins up
 * its own ColorModeProvider that is never told about later darkMode changes
 * (no ApplyTheme-style sync effect runs inside it) — it writes the stale
 * frozen mode's class onto the shared `document.body` on every mount/remount,
 * fighting the live top-level provider. See (2840) for the resulting bug:
 * text/borders using `color: 'inherit'` etc. would inherit whatever mode
 * `body` last got stuck on instead of the real live mode.
 * @returns {object} theme
 */
export function useDeepMerge() {
    const { darkMode } = useSelector((state) => state.settings);
    const [initialDarkMode] = useState(darkMode);
    const theme = {
        ...chakratheme,
        transitions: muiDefaults.transitions,
        zIndex: muiDefaults.zIndex,
        config: {
            ...chakratheme.config,
            initialColorMode: initialDarkMode ? 'dark' : 'light',
        },
    };

    return { theme, chakratheme };
}
